// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';

import { getGoalProgressForUser, getMonthlyInteractionsForLoggedInUser } from '../../api/productivity';
import { setLastUsedClinic } from '../../utils/clinicPersistence';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    loading: true,
    email: null,
    userId: 0,
    userMetadata: {
        isProviderAccount: false,
        providerId: null,
        firstName: '',
        lastName: ''
    },
    /*
    clinicGrants: {
        1: {
            meta: {
                name: 'Clinic 1',
            },
            grants: {
                CLINIC_MANAGER: true,
            }
            ...
        },
        ...
    }
    organizationMeta: {
        meta: {
            id: 1,
            name: 'SynerGrx'
        }
        grants: {
            ORG_USER: true
        }
        ...
    ...
    }
    */
    clinicGrants: {},
    organizationMeta: {},
    // every org membership of the logged-in account: [{ id, name }]; the
    // switcher renders the entries other than organizationMeta.meta.id
    organizations: [],
    // string id --> default to 0 to avoid writing non numbers for replacing id
    selectedClinic: '0',
    customRangeProviderCareStats: {
        loaded: false,
        error: false,
        needsRefresh: false,
        // identifies the period the in-flight request is for, so a slow response for a
        // period the user has already switched away from is ignored
        rangeKey: null,
        // goalHours is null until first load and 0 when the period has no goal configured
        custom: {
            secondsLogged: 0,
            goalHours: null
        }
    },
    monthlyInteractions: {
        loaded: false,
        error: false,
        month: {
            patientsCaredFor: 0
        }
    }
};

const slice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        setAccount(state, action) {
            state.loading = false;
            state.email = action.payload.email;
            state.userId = action.payload.userId;
            state.clinicGrants = action.payload.clinicGrants;
            state.organizationMeta = action.payload.organizationMeta;
            state.organizations = action.payload.organizations;
            state.userMetadata = action.payload.userMetadata;
        },
        setClinic(state, action) {
            state.selectedClinic = action.payload;
        },

        setMonthlyInteractions(state, action) {
            state.monthlyInteractions.loaded = true;
            state.monthlyInteractions.error = false;
            state.monthlyInteractions.month = action.payload.month;
        },
        setMonthlyInteractionsError(state, action) {
            state.monthlyInteractions.loaded = true;
            state.monthlyInteractions.error = action.payload;
        },

        setCustomRangeProviderCareStats(state, action) {
            if (action.payload.rangeKey !== state.customRangeProviderCareStats.rangeKey) {
                return;
            }
            state.customRangeProviderCareStats.loaded = true;
            state.customRangeProviderCareStats.error = false;
            state.customRangeProviderCareStats.needsRefresh = false;
            state.customRangeProviderCareStats.custom = action.payload.custom;
        },
        setCustomRangeProviderCareStatsLoading(state, action) {
            state.customRangeProviderCareStats.rangeKey = action.payload;
            state.customRangeProviderCareStats.loaded = false;
            state.customRangeProviderCareStats.error = false;
            state.customRangeProviderCareStats.needsRefresh = false;
        },
        setCustomRangeProviderCareStatsError(state, action) {
            if (action.payload.rangeKey !== state.customRangeProviderCareStats.rangeKey) {
                return;
            }
            state.customRangeProviderCareStats.loaded = true;
            state.customRangeProviderCareStats.needsRefresh = false;
            state.customRangeProviderCareStats.error = action.payload.message;
        },
        setCustomRangeProviderCareStatsNeedsRefresh(state) {
            state.customRangeProviderCareStats.needsRefresh = true;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function setAccountData(email, userId, clinicGrants, organizationMeta, userMetadata, organizations) {
    return async () => {
        dispatch(
            slice.actions.setAccount({
                userId,
                email,
                clinicGrants,
                organizationMeta,
                organizations,
                userMetadata
            })
        );
    };
}

export function setClinic(clinic) {
    return async (_, getState) => {
        const orgId = getState().account.organizationMeta?.meta?.id;
        dispatch(slice.actions.setClinic(clinic));
        setLastUsedClinic(orgId, clinic);
    };
}

export function getMonthlyInteractions() {
    return async () => {
        const response = await getMonthlyInteractionsForLoggedInUser();
        if (response.status === 200) {
            dispatch(slice.actions.setMonthlyInteractions(response.data));
        } else {
            dispatch(slice.actions.setMonthlyInteractionsError(response.message));
        }
    };
}

// period: day | week | month — the server resolves the window and the goal
export function getGoalPeriodProviderCareStats(period) {
    return async (_, getState) => {
        const rangeKey = period;
        dispatch(slice.actions.setCustomRangeProviderCareStatsLoading(rangeKey));
        const { userId } = getState().account;
        const response = await getGoalProgressForUser(userId, period);
        if (response.status === 200) {
            dispatch(
                slice.actions.setCustomRangeProviderCareStats({
                    rangeKey,
                    custom: { secondsLogged: response.data.secondsLogged, goalHours: response.data.goalHours }
                })
            );
        } else {
            dispatch(slice.actions.setCustomRangeProviderCareStatsError({ rangeKey, message: response.message }));
        }
    };
}

export function markRefreshNeededOfCustomProviderCareStats() {
    dispatch(slice.actions.setCustomRangeProviderCareStatsNeedsRefresh());
}
